import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle, Clock, AlertCircle, FileText, Download, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const RecipisseDemande = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [demande, setDemande] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDemande = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/demandes`, { withCredentials: true });
        const found = res.data.find(d => d.id === id);
        if (found) {
          setDemande(found);
        } else {
          toast.error("Dossier introuvable ou accès non autorisé");
        }
      } catch (e) {
        toast.error("Erreur lors de la récupération du dossier");
      } finally {
        setLoading(false);
      }
    };
    fetchDemande();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }

  if (!demande) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h2 className="text-xl font-bold mb-4">Dossier introuvable</h2>
        <Link to="/citoyen"><Button>Retour à mon espace</Button></Link>
      </div>
    );
  }

  return (
    <div className="bg-slate-100 min-h-screen py-10 print:bg-white print:py-0">
      <div className="container mx-auto max-w-3xl px-4">
        <div className="flex justify-between items-center mb-6 print:hidden">
          <Link to="/citoyen" className="text-slate-500 hover:text-[#0A192F] font-medium flex items-center gap-2 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour au tableau de bord
          </Link>
          <Button onClick={handlePrint} className="bg-[#0A192F] hover:bg-[#1E3A8A] text-white">
            <Printer className="w-4 h-4 mr-2" /> Imprimer le récépissé
          </Button>
        </div>

        {/* Printable Area */}
        <div className="bg-white p-10 md:p-14 rounded-2xl shadow-xl border border-slate-200 print:shadow-none print:border-none print:p-0 relative overflow-hidden" id="printable-receipt">
          {/* Print only header */}
          <div className="hidden print:flex justify-between items-center border-b-2 border-[#0A192F] pb-6 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center font-bold text-[#0A192F] text-xl border-2 border-[#0A192F]">LS</div>
              <div>
                <h1 className="font-bold text-xl text-[#0A192F] tracking-tight uppercase">Lualaba Services</h1>
                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">République Démocratique du Congo</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-slate-800">Date d'édition :</p>
              <p className="text-sm text-slate-600">{new Date().toLocaleDateString('fr-FR')}</p>
            </div>
          </div>

          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-4 -mt-4 print:hidden"></div>
          
          <div className="text-center mb-10 relative z-10">
            <div className="inline-block p-4 rounded-full bg-emerald-50 border border-emerald-100 mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-extrabold text-[#0A192F] tracking-tight mb-2">Récépissé de Dépôt</h2>
            <p className="text-slate-500 font-medium">Conservez précieusement ce document ou le numéro de référence.</p>
          </div>

          <div className="bg-[#0A192F] text-white rounded-xl p-8 mb-10 text-center shadow-inner print:bg-slate-100 print:text-[#0A192F] print:border print:border-slate-300">
            <p className="text-blue-200 font-semibold uppercase tracking-widest text-sm mb-2 print:text-slate-500">Numéro de Référence du Dossier</p>
            <p className="text-4xl sm:text-5xl font-mono font-black tracking-[0.2em]">{demande.reference}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 mb-10 border-b border-slate-100 pb-10">
            <div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Date de Dépôt</h4>
              <p className="text-lg font-bold text-slate-900">{new Date(demande.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Demandeur</h4>
              <p className="text-lg font-bold text-slate-900">{user?.name}</p>
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Secteur / Catégorie</h4>
              <p className="text-lg font-bold text-slate-900">{demande.secteur}</p>
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Localisation</h4>
              <p className="text-lg font-bold text-slate-900">{demande.localisation}</p>
            </div>
          </div>

          <div className="mb-10">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Objet de la démarche</h4>
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
              <h5 className="font-bold text-slate-900 mb-2">{demande.sujet}</h5>
              <p className="text-slate-700 leading-relaxed text-sm">{demande.description}</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm text-blue-900 flex items-start gap-3 mb-10">
            <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold mb-1">Comment suivre votre dossier ?</p>
              <p>Rendez-vous sur la page d'accueil de Lualaba Services et cliquez sur "Suivre mon dossier". Saisissez votre numéro de référence <span className="font-mono font-bold">({demande.reference})</span> pour connaître l'état d'avancement de votre demande en temps réel.</p>
            </div>
          </div>
          
          {demande.fichier_joint && (
            <div className="mb-10 print:hidden">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Pièce justificative jointe</h4>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Document attaché au dépôt</p>
                    <p className="text-xs text-slate-500">Stocké de manière sécurisée</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="bg-white"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${demande.fichier_joint}`, '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" /> Voir le document
                </Button>
              </div>
            </div>
          )}

          {/* Footer signature area for print */}
          <div className="hidden print:flex justify-between items-end mt-16 pt-8 border-t border-slate-200">
            <div className="text-xs text-slate-500 max-w-xs">
              Document généré électroniquement par Lualaba Services. Ne requiert pas de signature manuscrite pour le suivi citoyen.
            </div>
            <div className="text-center">
              <div className="w-48 h-px bg-slate-300 mb-2"></div>
              <p className="text-sm font-bold text-slate-700">Signature / Cachet</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipisseDemande;
